import React, { useMemo, useState } from 'react';
import { Project } from '../../types/project';
import { Task } from '../../types/task';
import { useTasks } from '../../hooks/useTasks';
import TaskView from '../tasks/TaskView';
import TaskForm from '../tasks/TaskForm';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
  CalendarIcon,
  PencilIcon,
  TrashIcon,
  ChatBubbleLeftIcon
} from '@heroicons/react/24/outline';

interface ProjectListViewProps {
  project: Project;
}

const ProjectListView: React.FC<ProjectListViewProps> = ({ project }) => {
  const { tasks, loading, updateTask, deleteTask } = useTasks();
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [deletingTask, setDeletingTask] = useState<Task | null>(null);

  // Projekt feladatainak szűrése
  const projectTasks = useMemo(() => {
    return tasks.filter(task => task.projectId === project.id);
  }, [tasks, project.id]);

  // Státusz szín
  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      todo: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
      in_progress: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
      completed: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      cancelled: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
    };
    return colors[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
  };

  // Prioritás szín
  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      urgent: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
      high: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
      medium: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
      low: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
    };
    return colors[priority] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
  };

  // Prioritás border szín
  const getPriorityBorderColor = (priority?: string) => {
    const colors: Record<string, string> = {
      urgent: 'border-l-red-500',
      high: 'border-l-orange-500',
      medium: 'border-l-yellow-500',
      low: 'border-l-green-500'
    };
    return priority ? colors[priority] || 'border-l-gray-300 dark:border-l-gray-600' : 'border-l-gray-300 dark:border-l-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Betöltés...</div>
      </div>
    );
  }

  return (
    <div className="h-full">
      {/* List Header */}
      <div className="mb-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
          {project.name} - Lista nézet
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {projectTasks.length} feladat összesen
        </p>
      </div>

      {/* Summary */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">To Do</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
            {projectTasks.filter(t => t.status === 'todo').length}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">In Progress</div>
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
            {projectTasks.filter(t => t.status === 'in_progress').length}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Completed</div>
          <div className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
            {projectTasks.filter(t => t.status === 'completed').length}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Cancelled</div>
          <div className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
            {projectTasks.filter(t => t.status === 'cancelled').length}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 border-b-2 border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Feladat
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Státusz & Prioritás
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Felelős
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Határidő & Idő
                </th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Műveletek
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {projectTasks.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    Nincs feladat
                  </td>
                </tr>
              ) : (
                projectTasks.map(task => (
                  <tr 
                    key={task.id} 
                    onClick={() => setSelectedTask(task)}
                    className={`border-l-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${getPriorityBorderColor(task.priority)}`}
                  >
                    {/* Feladat */}
                    <td className="px-6 py-4">
                      <div className="flex items-start">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {task.title}
                          </div>
                          {task.description && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
                              {task.description}
                            </div>
                          )}
                          {task.taskNumber && (
                            <div className="text-xs text-gray-400 dark:text-gray-500 mt-1 font-mono">
                              {task.taskNumber}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Státusz & Prioritás */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-2 items-start">
                        <span className={`px-3 py-1 text-xs leading-5 font-semibold rounded-full ${getStatusColor(task.status)}`}>
                          {task.status}
                        </span>
                        {task.priority && (
                          <span className={`px-3 py-1 text-xs leading-5 font-semibold rounded-full ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Felelős */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {task.assignedToName ? (
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mr-2">
                            <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                              {task.assignedToName.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="text-sm text-gray-900 dark:text-gray-100">{task.assignedToName}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </td>

                    {/* Határidő */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {task.dueDate ? (
                        <div className="flex items-center text-sm text-gray-900 dark:text-gray-100">
                          <CalendarIcon className="h-4 w-4 mr-2 text-gray-400 dark:text-gray-500" />
                          {new Date(task.dueDate).toLocaleDateString('hu-HU')}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </td>

                    {/* Műveletek */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingTask(task);
                          }}
                          className="p-2 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                          title="Szerkesztés"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingTask(task);
                          }}
                          className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Törlés"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Task Detail Modal */}
      {selectedTask && !editingTask && (
        <TaskView
          task={selectedTask}
          isOpen={!!selectedTask}
          onClose={() => setSelectedTask(null)}
          onEdit={(task) => setEditingTask(task)}
        />
      )}

      {/* Task Edit Form */}
      {editingTask && (
        <TaskForm
          initialData={editingTask}
          isOpen={!!editingTask}
          onClose={() => {
            setEditingTask(null);
            setSelectedTask(null);
          }}
          onSubmit={async (taskData) => {
            try {
              await updateTask(editingTask.id, taskData);
              setEditingTask(null);
              setSelectedTask(null);
            } catch (error) {
              console.error('Error updating task:', error);
            }
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deletingTask && (
        <ConfirmDialog
          isOpen={!!deletingTask}
          title="Feladat törlése"
          message={`Biztosan törölni szeretnéd a "${deletingTask.title}" feladatot? Ez a művelet nem vonható vissza.`}
          confirmText="Törlés"
          cancelText="Mégse"
          type="danger"
          onConfirm={async () => {
            try {
              await deleteTask(deletingTask.id);
              setDeletingTask(null);
            } catch (error) {
              console.error('Error deleting task:', error);
            }
          }}
          onClose={() => setDeletingTask(null)}
        />
      )}
    </div>
  );
};

export default ProjectListView;
